import React, { Fragment, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  Menu,
  Box,
  MenuItem,
  useMediaQuery,
  Theme,
  Divider,
} from '@material-ui/core';

import {
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Settings as SettingsIcon,
} from '@material-ui/icons';

import styles from './MainMenu.styles';
import getPersonDisplayName from '../../person/utils';
import { LoginInfo } from '../../login/types';
import {
  MainDispatchFunc,
  MessageBoxCommand,
  OpenMessageBoxArgs,
  OpenSettingPopupArgs,
} from '../types';
import config from '../../../../config';

type SubMenuDef = {
  title: string;
  action: () => void;
  icon?: JSX.Element | JSX.Element[],
  disabled: boolean,
};

type MenuDef = {
  title: string;
  subMenus?: (SubMenuDef | undefined)[];
};

type Props = {
  loginStatus?: LoginInfo;
  openMessageBox: MainDispatchFunc<OpenMessageBoxArgs>;
  openSettingsPopup: MainDispatchFunc<OpenSettingPopupArgs>;
};

const useStyles = makeStyles(styles);

const MainMenu: React.FC<Props> = ({
  loginStatus,
  openMessageBox,
  openSettingsPopup,
}: Props) => {
  const classes = useStyles();
  const [menuAnchor, setMenuAnchor] = useState<Element | undefined>();
  const [currentMenu, setCurrentMenu] = useState<string>();
  const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const yesAction = {
    title: 'Yes',
    color: 'secondary',
    action: () => {
      localStorage.removeItem(`site-${config.siteId}-api_token`);
      const loginUrl = `${config.routerBase}/login`;
      window.location.href = `${loginUrl}`;
    },
  } as MessageBoxCommand;

  const noAction = {} as MessageBoxCommand;

  const logOutConfirm = () => {
    openMessageBox({
      title: 'Confirm',
      content: 'Do you want to logout?',
      blurBackdrop: true,
      dismissText: 'Cancel',
      commands: [yesAction],
      onDismiss: noAction.action,
    });
  };

  const menuDefs: MenuDef[] = [{
    title: 'Main',
    subMenus: [
      {
        title: 'Settings',
        icon: <SettingsIcon fontSize="small" className={classes.menuItemIcon} />,
        // disabled: loginStatus?.data?.mode === 'person',
        disabled: false,
        action: () => openSettingsPopup({
          userId: loginStatus?.data?.userId!,
          username: loginStatus?.data?.username!,
          userPersonName: {
            ...loginStatus?.data?.userPersonName,
          },
          userPosition: loginStatus?.data?.userPosition,
          userType: loginStatus?.data?.userType,
        }),
      }, {
        title: 'Logout',
        icon: <ExitToAppIcon fontSize="small" className={classes.menuItemIcon} />,
        disabled: false,
        action: () => logOutConfirm(),
      },
    ],
  }];

  return (
    <Box
      className={classes.root}
      onMouseLeave={() => {
        setMenuAnchor(undefined);
      }}
    >
      {menuDefs.map((menuDef) => (
        <Fragment key={menuDef.title}>
          <Button
            size="large"
            variant="text"
            onClick={(event) => {
              setMenuAnchor(event.currentTarget);
              setCurrentMenu(menuDef.title);
            }}
            onMouseEnter={(event) => {
              setMenuAnchor(event.currentTarget);
              setCurrentMenu(menuDef.title);
            }}
          >
            <AccountCircleIcon />
            {isLargeScreen && (
              <Typography className={classes.userProfileText}>
                {loginStatus
                  ? getPersonDisplayName(loginStatus.data?.userPersonName)
                  : ''}
              </Typography>
            )}
          </Button>
          {menuDef.subMenus && (
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor) && currentMenu === menuDef.title}
              onClose={() => setMenuAnchor(undefined)}
              PaperProps={{
                onMouseLeave: () => {
                  setMenuAnchor(undefined);
                },
                style: {
                  pointerEvents: 'auto',
                },
              }}
              PopoverClasses={{ root: classes.menuPopoverRoot }}
              hideBackdrop
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              {menuDef.subMenus?.filter((item): item is SubMenuDef => !!item).map(
                (subMenu, index) => (
                  [
                    (menuDef.subMenus && index + 1 === menuDef.subMenus.length) && (
                      <Divider />
                    ),
                    <MenuItem
                      key={subMenu.title}
                      onClick={() => subMenu.action()}
                      className={classes.menuItem}
                      disabled={subMenu.disabled}
                    >
                      {subMenu.icon}
                      {subMenu.title}
                    </MenuItem>,
                  ]
                ),
              )}
            </Menu>
          )}
        </Fragment>
      ))}
    </Box>
  );
};

export default MainMenu;
